
import Vue, { PropType } from 'vue';
import _debounce from 'lodash/debounce';
import { auth } from '@/firebase';
import { MEMBERS_API_BASE_URL, MemberRow, MembersFetchResult, fetchMembersPage } from '@/utils/membersApi';
import { EmailFieldEntries, emailFieldEntries, memberEmailPickerItems } from '@/components/Filters/companyMemberPickerItems';

// How many matches the list offers at a time. Typing narrows it on the server.
const PICKER_PAGE_SIZE = 50;

/**
 * A Filters box field for email addresses: the company's Teams' Member. Two ways
 * in. Pick people from the list, which searches the company's people on the
 * server as you type (the Directory's `/members`), so it offers people who have
 * only been invited as well as joined ones, and people whose only Teams are
 * deactivated. Or type any address, or paste several, which the field takes
 * whether or not the list has them. It shows and holds several, as Boards'
 * Owner does (TeamMemberPickerField.vue). Typed text that is not an address is
 * not taken: it stays in the field, marked, to be fixed. An address is its own
 * label, so a pick stays named when the Filters box rebuilds the field.
 */
export default Vue.extend({
	name: 'CompanyMemberPickerField',
	props: {
		value: { type: Array as PropType<string[]>, default: () => [] },
		companyId: { type: String, default: null },
		// The account the request acts as: the caller's selected Team
		accountTeamId: { type: String, default: null },
		placeholder: { type: String, default: 'Member' },
		// The Owner field's, so the two read as the same kind of field
		icon: { type: String, default: 'mdi-account-outline' },
	},
	data () {
		return {
			search: null as string | null,
			// Typed text the field would not take, given back to be fixed
			rejectedText: null as string | null,
			found: [] as string[],
			loading: false,
			loadedCompanyId: null as string | null,
			requestVersion: 0,
			debouncedFetch: null as ((q: string) => void) | null,
		};
	},
	computed: {
		items (): string[] {
			return memberEmailPickerItems(this.value, this.found);
		},
		// Marked until the text it gave back is changed
		invalidEntry (): boolean {
			return !!this.rejectedText && (this.search || '').trim() === this.rejectedText;
		},
	},
	watch: {
		search (newVal: string | null, oldVal: string | null) {
			if ((newVal || '') === (oldVal || '')) return;
			this.clearHighlightedRow();
			if (this.debouncedFetch) this.debouncedFetch(newVal || '');
		},
		companyId (newVal: string | null, oldVal: string | null) {
			if (newVal === oldVal) return;
			// Another company's people are not this one's to offer
			this.found = [];
			this.loadedCompanyId = null;
		},
	},
	created () {
		this.debouncedFetch = _debounce((q: string) => this.fetchMatches(q), 300);
	},
	mounted () {
		// The native event, which comes as the mouse goes down on whatever took the focus,
		// before that thing's click
		const input: HTMLInputElement | undefined = this.comboboxInput();
		if (input) input.addEventListener('blur', this.commitTyped);
	},
	beforeDestroy () {
		const debounced = this.debouncedFetch as unknown as { cancel?: () => void } | null;
		if (debounced && debounced.cancel) debounced.cancel();
		const input: HTMLInputElement | undefined = this.comboboxInput();
		if (input) input.removeEventListener('blur', this.commitTyped);
	},
	methods: {
		/**
		 * A pick or typed text arrives here. Only addresses are kept; anything else
		 * goes back into the field. Clearing the field hands back an empty list,
		 * which the Filters box reads as no filter.
		 */
		onInput (next: string[] | null) {
			const entries: EmailFieldEntries = emailFieldEntries(Array.isArray(next) ? next : []);
			this.$emit('input', entries.addresses);
			if (!entries.rejected.length) return;
			const rejectedText: string = entries.rejected.join(', ');
			// After the combobox has cleared its search for the entry it just took
			this.$nextTick(() => {
				this.rejectedText = rejectedText;
				this.search = rejectedText;
			});
		},
		comboboxInput (): HTMLInputElement | undefined {
			const combobox = this.$refs.combobox as Vue | undefined;
			return combobox ? combobox.$refs.input as unknown as HTMLInputElement | undefined : undefined;
		},
		/**
		 * Leaving the field adds what was typed, at once. Vuetify adds it too, but a
		 * tick after the focus goes, which is after the click that took it: the
		 * Filters box's Apply would already have read the draft without it. Its own
		 * search is cleared first, so it does not add the same text again.
		 */
		commitTyped () {
			const typed: string = (this.search || '').trim();
			if (!typed) return;
			const combobox = this.$refs.combobox as unknown as { internalSearch?: string | null } | undefined;
			if (combobox) combobox.internalSearch = null;
			this.onInput([...(this.value || []), typed]);
		},
		/**
		 * After a pick, Vuetify keeps that row highlighted, and a highlighted row
		 * is what Enter toggles and why a comma drops what was typed. Typing starts
		 * from no highlighted row, so what was typed is what gets added.
		 */
		clearHighlightedRow () {
			const combobox = this.$refs.combobox as unknown as { setMenuIndex?: (index: number) => void } | undefined;
			if (combobox && combobox.setMenuIndex) combobox.setMenuIndex(-1);
		},
		// The first page waits until the field is used, so an unopened box costs nothing
		loadFirstPage () {
			if (this.loadedCompanyId === this.companyId) return;
			this.fetchMatches(this.search || '');
		},
		async fetchMatches (q: string) {
			if (!this.companyId) return;
			const version: number = ++this.requestVersion;
			const companyId: string = this.companyId;
			this.loading = true;
			let result: MembersFetchResult;
			try {
				const currentUser = auth.currentUser;
				const idToken: string | null = currentUser ? await currentUser.getIdToken() : null;
				result = idToken
					? await fetchMembersPage({
						baseUrl: MEMBERS_API_BASE_URL,
						idToken,
						request: {
							companyId,
							accountTeamId: this.accountTeamId,
							limit: PICKER_PAGE_SIZE,
							q: q || null,
						},
					})
					: { ok: false, reason: 'unauthorized' };
			} catch (e) {
				console.error('Error searching members:', e);
				result = { ok: false, reason: 'failed' };
			}
			if (version !== this.requestVersion) return;
			this.loading = false;
			// The company changed while this was in flight: these are not its people
			if (companyId !== this.companyId) return;
			if (result.ok === true) {
				this.loadedCompanyId = companyId;
				this.found = result.page.rows.map((row: MemberRow) => row.email);
			}
		},
	},
});
